import React, { useContext, useEffect, useRef, useState } from "react";
import { InfiosStage } from "./Stage.style";
import Button from "../Button/Button";
import { colors, devices, sizes } from "../../utils/styled/_variables.styled";
import Link from "../Link/Link";
import Icon from "../Icon/Icon";
import { ErrorBoundary } from "react-error-boundary";
import { urlHasParam } from "../../utils/js/params";
import axios from "axios";
import useVisibility from "../../utils/useVisibility";
import Video from "../../utils/Video";
import { StateContext } from "../../context/stateContext";
import objectHash from "object-hash";
import { getWidthAndHeightProps } from "../../utils/js/widthAndHeight";
import { useCheckBreakpoint } from "../../utils/styled/_mixins.style";

const StageV2 = (props) => {
	const {
		buttonsList,
		componentType,
		title,
		titleColor,
		leadText,
		leadTextColor,
		callToAction,
		video,
		videoUrl,
		teaserPath,
		pageLanguage,
		infoTypeTime,
		contentType,
		overviewPagePath,
		overviewPageTarget,
		readingTime,
		showArrow,
		hideTeaserTile,
		alt,
		rightText,
		rightTextArrow,
		rightLink,
		rightId,
		rightTarget,
		desktopPictureCrops,
		tabletPictureCrops,
		mobilePictureCrops,
		desktopPathImage,
		tabletPathImage,
		mobilePathImage,
		desktopAnimation,
		desktopPathAnimation,
		tabletAnimation,
		tabletPathAnimation,
		mobileAnimation,
		mobilePathAnimation,
		desktopVideo,
		desktopVideoUrl,
		tabletVideo,
		tabletVideoUrl,
		mobileVideo,
		mobileVideoUrl,
		desktopAlt,
		tabletAlt,
		mobileAlt,
		desktopType,
		desktopImageUploadType,
		tabletType,
		tabletImageUploadType,
		mobileType,
		mobileImageUploadType,
		mobileVideoUploadType,
		tabletVideoUploadType,
		desktopVideoUploadType,
		mobileAnimationUploadType,
		tabletAnimationUploadType,
		desktopAnimationUploadType,
	} = props;

	const [arrowPosition, setArrowPosition] = useState(0);
	const [viewHeight, setViewHeight] = useState(
		window.innerHeight ||
			document.documentElement.clientHeight ||
			document.body.clientHeight
	);
	const [toScroll, setToScroll] = useState(null);
	const sectionRef = useRef(null);
	const contentRef = useRef(null);
	const [teaser, setTeaser] = useState({});

	const [stageRef, isStageVisible] = useVisibility();
	const { breadcrumb } = useContext(StateContext);

	const isMobile = useCheckBreakpoint(sizes.mobile);
	const isTablet = useCheckBreakpoint(sizes.tabletLarge);

	useEffect(() => {
		window.addEventListener("resize", handleResize);
		window.addEventListener("orientationchange", handleResize);

		setToScroll(sectionRef.current.getBoundingClientRect().bottom);
		handleResize();

		return () => {
			window.removeEventListener("resize", handleResize);
			window.removeEventListener("orientationchange", handleResize);
		};
	}, []);

	useEffect(() => {
		const teaserPath = props.teaserPath;
		if (teaserPath) {
			if (teaserPath.startsWith("/content/dam")) {
				axios
					.get((teaserPath + ".json").replace("/content/dam", "/api/assets"))
					.then(
						({
							data: {
								properties: { elements },
							},
						}) => {
							const getValue = ({ variations, value }) =>
								variations[pageLanguage]
									? variations[pageLanguage].value
									: value;

							setTeaser({
								title: getValue(elements.title),
								text: getValue(elements.text),
								callToAction: getValue(elements.callToAction),
								callToActionLink: getValue(elements.callToActionLink),
								openOnANewBrowserTab: getValue(elements.openOnANewBrowserTab),
							});
						}
					)
					.catch((error) => {
						console.error(error);
					});
			} else {
				let teaserEndPoint = `${encodeURIComponent(
					`getTeasers;currentDate=${new Date().toISOString()};variation=${pageLanguage}`
				)}`;
				axios
					.get(`/graphql/execute.json/koerber/${teaserEndPoint}`)
					.then(
						({
							data: {
								data: {
									teaserList: { items },
								},
							},
						}) => {
							setTeaser(items.length > 0 ? items[0] : {});
						}
					)
					.catch((error) => console.error(error));
			}
		} else {
			let teaserEndPoint = `${encodeURIComponent(
				`getTeasers;currentDate=${new Date().toISOString()};variation=${pageLanguage}`
			)}`;
			axios
				.get(`/graphql/execute.json/koerber/${teaserEndPoint}`)
				.then(
					({
						data: {
							data: {
								teaserList: { items },
							},
						},
					}) => {
						setTeaser(items.length > 0 ? items[0] : {});
					}
				)
				.catch((error) => console.error(error));
		}
	}, [props.cqPath, props.teaserPath]);

	const handleResize = () => {
		setViewHeight(
			window.innerHeight ||
				document.documentElement.clientHeight ||
				document.body.clientHeight
		);

		const childrem = contentRef.current;
		const distance = childrem.offsetTop + childrem.offsetHeight;

		setArrowPosition(window.innerHeight - distance);
	};

	const scrollToNextComponent = () => {
		window.scroll({ top: window.scrollY + toScroll - 88, behavior: "smooth" });
	};

	const getAnimation = () => {
		const currentBreakpoint = isMobile
			? "mobile"
			: isTablet
			? "tablet"
			: "desktop";

		if (
			currentBreakpoint === "mobile" &&
			(mobileAnimation || mobilePathAnimation)
		) {
			return {
				image:
					mobileAnimationUploadType === "dragAnimation"
						? mobileAnimation
						: mobilePathAnimation,
				alt: mobileAlt || "",
			};
		}

		if (
			currentBreakpoint === "tablet" &&
			(tabletAnimation || tabletPathAnimation)
		) {
			return {
				image:
					tabletAnimationUploadType === "dragAnimation"
						? tabletAnimation
						: tabletPathAnimation,
				alt: tabletAlt || "",
			};
		}

		if (desktopAnimation || desktopPathAnimation) {
			return {
				image:
					desktopAnimationUploadType === "dragAnimation"
						? desktopAnimation
						: desktopPathAnimation,
				alt: desktopAlt || "",
			};
		}

		return { image: "", alt: "" };
	};

	const getVideo = () => {
		const currentBreakpoint = isMobile
			? "mobile"
			: isTablet
			? "tablet"
			: "desktop";
		const effectiveType = getEffectiveType();

		if (effectiveType === "video") {
			if (currentBreakpoint === "mobile" && (mobileVideo || mobileVideoUrl)) {
				return mobileVideoUploadType === "dragVideo"
					? mobileVideo
					: mobileVideoUrl;
			}

			if (currentBreakpoint === "tablet" && (tabletVideo || tabletVideoUrl)) {
				return tabletVideoUploadType === "dragVideo"
					? tabletVideo
					: tabletVideoUrl;
			}

			return desktopVideoUploadType === "dragVideo"
				? desktopVideo
				: desktopVideoUrl;
		}

		return "";
	};

	const getImage = (size) => {
		const deviceType = isMobile ? "mobile" : isTablet ? "tablet" : "desktop";

		const getDeviceImage = (device) => {
			const [hasCropImages, uploadType, pictureCrops, pathImage, alt] =
				deviceProps[device];

			let stagePropStr = "";
			if (device === "mobile") {
				stagePropStr = `stage${size}Mobile`;
			} else if (device === "tablet") {
				stagePropStr = `stage${size}Tablet`;
			} else {
				stagePropStr = `stage${size}Desktop`;
			}

			if (uploadType === "drag" && pictureCrops && pictureCrops[stagePropStr]) {
				return { image: pictureCrops[stagePropStr], alt };
			}

			if (pathImage) {
				return { image: pathImage, alt };
			}

			if (device !== "desktop") {
				return getDeviceImage("desktop");
			}

			return "";
		};

		const deviceProps = {
			mobile: [
				mobileCropImages,
				mobileImageUploadType,
				mobilePictureCrops,
				mobilePathImage,
				mobileAlt,
			],
			tablet: [
				tabletCropImages,
				tabletImageUploadType,
				tabletPictureCrops,
				tabletPathImage,
				tabletAlt,
			],
			desktop: [
				desktopCropImages,
				desktopImageUploadType,
				desktopPictureCrops,
				desktopPathImage,
				desktopAlt,
			],
		};

		return getDeviceImage(deviceType);
	};

	const hasImageContent = (breakpoint) => {
		if (breakpoint === "mobile") {
			return (
				(mobilePictureCrops && Object.keys(mobilePictureCrops).length > 0) ||
				mobilePathImage
			);
		} else if (breakpoint === "tablet") {
			return (
				(tabletPictureCrops && Object.keys(tabletPictureCrops).length > 0) ||
				tabletPathImage
			);
		} else {
			return (
				(desktopPictureCrops && Object.keys(desktopPictureCrops).length > 0) ||
				desktopPathImage
			);
		}
	};

	const hasGifContent = (breakpoint) => {
		if (breakpoint === "mobile") {
			return mobileAnimation || mobilePathAnimation;
		} else if (breakpoint === "tablet") {
			return tabletAnimation || tabletPathAnimation;
		} else {
			return desktopAnimation || desktopPathAnimation;
		}
	};

	const hasVideoContent = (breakpoint) => {
		if (breakpoint === "mobile") {
			return mobileVideo || mobileVideoUrl;
		} else if (breakpoint === "tablet") {
			return tabletVideo || tabletVideoUrl;
		} else {
			return desktopVideo || desktopVideoUrl;
		}
	};

	const hasContent = (breakpoint, type) => {
		if (type === "image") return hasImageContent(breakpoint);
		if (type === "gif") return hasGifContent(breakpoint);
		if (type === "video") return hasVideoContent(breakpoint);
		return false;
	};

	const getEffectiveType = () => {
		const currentBreakpoint = isMobile
			? "mobile"
			: isTablet
			? "tablet"
			: "desktop";
		const declaredType = isMobile
			? mobileType
			: isTablet
			? tabletType
			: desktopType;

		if (
			currentBreakpoint === "desktop" ||
			hasContent(currentBreakpoint, declaredType)
		) {
			return declaredType;
		}

		if (hasContent("desktop", desktopType)) {
			return desktopType;
		}

		return declaredType;
	};

	useEffect(() => {
		if (sectionRef.current) {
			sectionRef.current.parentElement.classList.add(`unset__grid`);
			sectionRef.current.style.opacity = "100%";
		}
	}, []);

	const desktopCropImages =
		isStageVisible &&
		((desktopPictureCrops && Object.keys(desktopPictureCrops).length) ||
			desktopPathImage);
	const tabletCropImages =
		isStageVisible &&
		((tabletPictureCrops && Object.keys(tabletPictureCrops).length) ||
			tabletPathImage);
	const mobileCropImages =
		isStageVisible &&
		((mobilePictureCrops && Object.keys(mobilePictureCrops).length) ||
			mobilePathImage);
	const stageSize =
		componentType === "large" ? "L1" : componentType === "small" ? "L2" : null;

	function LeadTextFormatted({ leadText }) {
					if (!leadText) return null;
          const formattedLeadText = leadText.split('\n').map((line, index) => (
              <React.Fragment key={index}>
                  {line}
                  <br />
              </React.Fragment>
          ));
          return <p className="leadText" style={{ color: leadTextColor }}>{formattedLeadText}</p>;

  }
	return (
		<InfiosStage
			style={{ opacity: "0%", position: "relative" }}
			picture={
				isStageVisible &&
				(desktopPictureCrops || tabletPictureCrops || mobilePictureCrops)
			}
			ref={sectionRef}
			hideTeaserTile={hideTeaserTile}
			viewHeight={viewHeight}
			teaserTitle={teaser.teaserTitle}
			arrowPosition={arrowPosition}
			leadTextColor={leadTextColor}
		>
			{(() => {
				if (!stageSize) return null;

				const effectiveType = getEffectiveType();

				if (effectiveType === "image" || effectiveType === "gif") {
					return (
						<picture>
							{effectiveType === "image" && (
								<>
									<source
										media={devices.mobile}
										srcSet={
											mobileImageUploadType === "drag" &&
											mobilePictureCrops &&
											mobilePictureCrops[`stage${stageSize}Mobile`]
												? mobilePictureCrops[`stage${stageSize}Mobile`]
												: mobilePathImage
												? mobilePathImage
												: desktopImageUploadType === "drag" &&
												  desktopPictureCrops &&
												  desktopPictureCrops[`stage${stageSize}Mobile`]
												? desktopPictureCrops[`stage${stageSize}Mobile`]
												: desktopPathImage || ""
										}
									/>
									<source
										media={devices.tabletLarge}
										srcSet={
											tabletImageUploadType === "drag" &&
											tabletPictureCrops &&
											tabletPictureCrops[`stage${stageSize}Tablet`]
												? tabletPictureCrops[`stage${stageSize}Tablet`]
												: tabletPathImage
												? tabletPathImage
												: desktopImageUploadType === "drag" &&
												  desktopPictureCrops &&
												  desktopPictureCrops[`stage${stageSize}Tablet`]
												? desktopPictureCrops[`stage${stageSize}Tablet`]
												: desktopPathImage || ""
										}
									/>
									<source
										media={devices.desktop}
										srcSet={
											desktopImageUploadType === "drag" &&
											desktopPictureCrops &&
											desktopPictureCrops[`stage${stageSize}Desktop`]
												? desktopPictureCrops[`stage${stageSize}Desktop`]
												: desktopPathImage || ""
										}
									/>
								</>
							)}

							<img
								{...getWidthAndHeightProps(sectionRef)}
								className="stage__bg"
								src={
									effectiveType === "gif"
										? getAnimation().image
										: getImage(stageSize).image
								}
								alt={
									effectiveType === "gif"
										? getAnimation().alt
										: getImage(stageSize).alt
								}
							/>
						</picture>
					);
				} else if (effectiveType === "video") {
					return (
						<Video
							key={`video-${
								isMobile ? "mobile" : isTablet ? "tablet" : "desktop"
							}-${getVideo()}`}
							className="stage__banner-video"
							src={getVideo()}
							autoPlay
							loop
							muted
							playsInline
						/>
					);
				}

				return null;
			})()}

			<div
				ref={stageRef}
				className={`stage ${
					componentType === "large" || componentType === "small"
						? " stage__padding"
						: ""
				}`}
			>
				<div
					className={`stage__grid stage${
						componentType === "large"
							? "__banner"
							: componentType === "small"
							? "__banner stage__banner-small"
							: "__content"
					} ${componentType === "large" ? "stage-l1" : ""}`}
				>
					<div
						className={`stage${
							componentType === "large" || componentType === "small"
								? "__banner-container"
								: "__content-container"
						}`}
						ref={contentRef}
					>
						<div
							className={`stage${
								componentType === "large" || componentType === "small"
									? "__banner-texts"
									: "__content-texts"
							}`}
						>
							{infoTypeTime === "true" && componentType !== "simple" && (
								<div className="large__type-time">
									{contentType && (
										<a
											className="label-small content__type"
											href={overviewPagePath}
											target={overviewPageTarget}
										>
											{contentType}
										</a>
									)}
									{readingTime && (
										<>
											<Icon icon="eye-filled" color={colors.gray4} />
											<p className="label-small content__time">
												{readingTime} min
											</p>
										</>
									)}
								</div>
							)}
							{componentType === "simple" && breadcrumb}
							<h1 className="heading-1" style={{ color: titleColor }}>
								{title}
							</h1>
              <LeadTextFormatted leadText={leadText} />
						</div>
						{(componentType === "large" || componentType === "small") &&
							buttonsList && (
								<div className="buttons__wrapper">
									{buttonsList.map((item, k) => {
										let stageClass =
											item.stageButtonPath === "#contact-form"
												? `stage-contact`
												: "";
										return (
											<a
												key={objectHash(item)}
												href={item.stageButtonPath}
												target={item.stageButtonTarget}
											>
												<Button
													id={item.stageButtonId || stageClass}
													className={`${item.stageButtonId || stageClass}`}
													text={item.stageButtonText}
													noIcon={item.noIcon}
													icon={item.icon}
													blue={item.stageButtonColor === "blue"}
													green={item.stageButtonColor === "green"}
													greenlight={item.stageButtonColor === "greenlight"}
													white={item.stageButtonColor === "white"}
												/>
											</a>
										);
									})}
								</div>
							)}
						{componentType === "large" && !showArrow && (
							<div className="arrow-animated">
								<svg
									xmlns="http://www.w3.org/2000/svg"
									width="40"
									height="35"
									viewBox="0 0 40 35"
									fill="none"
									onClick={scrollToNextComponent}
								>
									<path
										d="M19.6087 34.2178L0.0290099 0.304726L39.1884 0.304729L19.6087 34.2178Z"
										fill="#D0FF25"
									/>
								</svg>
							</div>
						)}
					</div>
					{rightText && componentType !== "simple" ? (
						<a
							href={rightLink}
							target={rightTarget}
							rel="noreferrer"
							className="stage__right-text"
							id={rightId || "rightLink"}
						>
							{rightTextArrow ? (
								<svg
									xmlns="http://www.w3.org/2000/svg"
									width="40"
									height="35"
									viewBox="0 0 40 35"
									fill="none"
								>
									<path
										d="M19.6087 34.2178L0.0290099 0.304726L39.1884 0.304729L19.6087 34.2178Z"
										fill="#D0FF25"
									/>
								</svg>
							) : null}
							<span>{rightText}</span>
						</a>
					) : null}
					{componentType === "large" && teaser.title && !hideTeaserTile && (
						<a
							href={teaser.callToActionLink}
							target={teaser.callToAction}
							className="stage__banner-box stage-teaser"
						>
							<div>
								<span className="heading-6">{teaser.title}</span>
								<p className="stage__banner-box-p p-regular">{teaser.text}</p>
							</div>
							<div className="stage__banner-box-link">
								<Link
									className="stage-teaser"
									link={teaser.callToActionLink}
									text={teaser.callToAction}
									target={teaser.openOnANewBrowserTab ? "_blank" : "_self"}
									animated
								/>
							</div>
						</a>
					)}
				</div>
			</div>
		</InfiosStage>
	);
};

const StageV2HandleErrors = (props) => {
	return (
		<ErrorBoundary
			fallback={
				props.isInEditor || urlHasParam("wcmmode=disabled") ? (
					<div
						style={{
							minHeight: "50px",
							display: "flex",
							alignItems: "center",
							backgroundColor: "#ffe1db",
						}}
					>
						<p>
							The stage component is currently experiencing errors. Please try
							again later.
						</p>
					</div>
				) : (
					<div></div>
				)
			}
		>
			<StageV2 {...props} />
		</ErrorBoundary>
	);
};

export default StageV2HandleErrors;
